/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {EventEmitter, forwardRef, Inject, Injectable} from '@angular/core';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import 'moment/min/locales';
import {createNumberMask} from 'text-mask-addons/dist/textMaskAddons';

import {DATE_FORMAT, DISPLAY_TYPE, GP_DATE_MASK, NUMBER_FORMAT} from '../../app.constants';
import {ApiService} from '../api';
import {User, UserService} from '../user/user.service';
import {isPresent} from '../utils/lang';

const defaultCurrency: string = 'EUR';

const moment = require('moment-timezone');

// ---------------------------------------------------------------------------------------------------------------------

// Copied from @angular/facade/src/intl.ts - they are unfortunately not exported anymore.

export enum NumberFormatStyle {
  Decimal,
  Percent,
  Currency
}

export class NumberFormatter {
  public static format(num: number,
                       locale: string,
                       style: NumberFormatStyle,
                       {
                         minimumIntegerDigits, minimumFractionDigits, maximumFractionDigits, currency,
                         currencyAsSymbol = false
                       }: {
                         minimumIntegerDigits?: number,
                         minimumFractionDigits?: number,
                         maximumFractionDigits?: number,
                         currency?: string,
                         currencyAsSymbol?: boolean
                       } = {}): string {
    const options: Intl.NumberFormatOptions = {
      minimumIntegerDigits,
      minimumFractionDigits,
      maximumFractionDigits,
      style: NumberFormatStyle[style].toLowerCase()
    };

    if (style === NumberFormatStyle.Currency) {
      options.currency = currency;
      options['currencyDisplay'] = currencyAsSymbol ? 'symbol' : 'code';
    }
    return new Intl.NumberFormat(locale, options).format(num);
  }
}

// ---------------------------------------------------------------------------------------------------------------------

/**
 * Internationalization service that handles server side i18n.
 */
@Injectable()
export class LocalizationService {
  public changeLanguageEvent: EventEmitter<any> = new EventEmitter<any>();
  public changeNumberFormatEvent: EventEmitter<any> = new EventEmitter<any>();
  public changeDateFormatEvent: EventEmitter<any> = new EventEmitter<any>();
  public currentLanguage: string;

  public dateMask = GP_DATE_MASK['YYYY-MM-DD'];

  public dateFormat: string = DATE_FORMAT;
  public numberDecimalMask;

  public numberIntegerMask;
  public numberFormat = NUMBER_FORMAT;

  private _BIDI_RTL_LANGS: string[] =
    [
      'ar', /* 'العربية', Arabic */
      'arc', /* Aramaic */
      'bcc', /* 'بلوچی مکرانی', Southern Balochi */
      'bqi', /* 'بختياري', Bakthiari */
      'ckb', /* 'Soranî / کوردی', Sorani */
      'dv', /* Dhivehi */
      'fa', /* 'فارسی', Persian */
      'glk', /* 'گیلکی', Gilaki */
      'he', /* 'עברית', Hebrew */
      'ku', /* 'Kurdî / كوردی', Kurdish */
      'mzn', /* 'مازِرونی', Mazanderani */
      'pnb', /* 'پنجابی', Western Punjabi */
      'ps', /* 'پښتو', Pashto, */
      'sd', /* 'سنڌي', Sindhi */
      'ug', /* 'Uyghurche / ئۇيغۇرچە', Uyghur */
      'ur', /* 'اردو', Urdu */
      'yi'  /* 'ייִדיש', Yiddish */
    ];

  constructor(@Inject(forwardRef(() => ApiService)) private api: ApiService,
              private userService: UserService,
              private translate: TranslateService) {

    this.currentLanguage = this.translate.currentLang;

    this.userService.getUser().subscribe((user: User) => {
      if (user.integerMask && user.decimalMask) {
        this.numberFormat = {
          separator: user.integerMask,
          decimal: user.decimalMask
        };
      }

      if (user.dateFormat) {
        this.dateFormat = user.dateFormat;
      }

      this.changeDateFormat(this.dateFormat);
      this.changeNumberFormat(this.numberFormat);
    });

    this.translate.onLangChange
      .subscribe((langChangeEvent: LangChangeEvent) => {
        this.currentLanguage = langChangeEvent.lang;

        switch (this.currentLanguage) {
          case 'fr-FR':
          case 'fr-LU':
            moment.locale('fr-fr');
            break;
          case 'ru-RU':
            moment.locale('ru');
            break;
          case 'ar-EG':
            moment.locale('ar-ly');
            break;
          case 'it-IT':
            moment.locale('it');
            break;
          case 'es-ES':
            moment.locale('es');
            break;
          case 'en-AU':
            moment.locale('en-au');
            break;
          case 'zh-CN':
            moment.locale('zh-cn');
            break;
          case 'nl-NL':
            moment.locale('nl');
            break;
          case 'pt-PT':
            moment.locale('pt');
            break;
          case 'en-GB':
            moment.locale('en-gb');
            break;
          case 'de-DE':
            moment.locale('de');
            break;
          case 'en-US':
          default:
            moment.locale('en');
            break;
        }
      });
  }

  /**
   * Localize a number according to a format and a locale.
   * @param value the number to format
   * @param gpFormat the GP-specific format to format the number in.
   * @param locale the locale to format the number in. If not provided, default is en-US.
   * @param currency the currency when formatting a currency. Default is EUR.
   *
   * @return {string} the formatted number
   */
  public localizeNumber(value: number,
                        gpFormat: string,
                        locale?: string,
                        currency?: string,
                        fractionDigits?: number): string {

    let format = NumberFormatStyle.Decimal;
    const options = {
      minimumIntegerDigits: 1,
      minimumFractionDigits: null,
      maximumFractionDigits: null,
      currency: undefined,
      currencyAsSymbol: false
    };
    switch (gpFormat) {
      case DISPLAY_TYPE.DECIMAL_ALL_DIGITS:
        options.maximumFractionDigits = 20;
        options.minimumFractionDigits = 0;
        break;
      case DISPLAY_TYPE.DECIMAL_1_DIGIT:
        options.maximumFractionDigits = 1;
        options.minimumFractionDigits = 1;
        break;
      case DISPLAY_TYPE.DECIMAL_2_DIGIT:
        options.maximumFractionDigits = 2;
        options.minimumFractionDigits = 2;
        break;
      case DISPLAY_TYPE.DECIMAL_3_DIGIT:
        options.maximumFractionDigits = 3;
        options.minimumFractionDigits = 3;
        break;
      case DISPLAY_TYPE.DECIMAL_4_DIGIT:
        options.maximumFractionDigits = 4;
        options.minimumFractionDigits = 4;
        break;
      case DISPLAY_TYPE.DECIMAL_5_DIGIT:
        options.maximumFractionDigits = 5;
        options.minimumFractionDigits = 5;
        break;
      case DISPLAY_TYPE.DECIMAL_6_DIGIT:
        options.maximumFractionDigits = 6;
        options.minimumFractionDigits = 6;
        break;
      case DISPLAY_TYPE.DECIMAL_7_DIGIT:
        options.maximumFractionDigits = 7;
        options.minimumFractionDigits = 7;
        break;
      case DISPLAY_TYPE.DECIMAL_8_DIGIT:
        options.maximumFractionDigits = 8;
        options.minimumFractionDigits = 8;
        break;
      case DISPLAY_TYPE.DECIMAL_9_DIGIT:
        options.maximumFractionDigits = 9;
        options.minimumFractionDigits = 9;
        break;
      case DISPLAY_TYPE.DECIMAL_10_DIGIT:
        options.maximumFractionDigits = 10;
        options.minimumFractionDigits = 10;
        break;
      case DISPLAY_TYPE.DECIMAL_UP_TO_4_DIGIT:
        format = NumberFormatStyle.Decimal;
        options.maximumFractionDigits = 4;
        options.minimumFractionDigits = 0;
        break;
      case DISPLAY_TYPE.INTEGER:
        options.maximumFractionDigits = 0;
        break;
      case DISPLAY_TYPE.CURRENCY:
      case DISPLAY_TYPE.CURRENCY_2_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 2;
        options.minimumFractionDigits = 2;
        break;
      case DISPLAY_TYPE.CURRENCY_1_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 1;
        options.minimumFractionDigits = 1;
        break;
      case DISPLAY_TYPE.CURRENCY_3_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 3;
        options.minimumFractionDigits = 3;
        break;
      case DISPLAY_TYPE.CURRENCY_4_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 4;
        options.minimumFractionDigits = 4;
        break;
      case DISPLAY_TYPE.CURRENCY_5_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 5;
        options.minimumFractionDigits = 5;
        break;
      case DISPLAY_TYPE.CURRENCY_6_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 6;
        options.minimumFractionDigits = 6;
        break;
      case DISPLAY_TYPE.CURRENCY_7_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 7;
        options.minimumFractionDigits = 7;
        break;
      case DISPLAY_TYPE.CURRENCY_8_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 8;
        options.minimumFractionDigits = 8;
        break;
      case DISPLAY_TYPE.CURRENCY_9_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 9;
        options.minimumFractionDigits = 9;
        break;
      case DISPLAY_TYPE.CURRENCY_10_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 10;
        options.minimumFractionDigits = 10;
        break;
      case DISPLAY_TYPE.CURRENCY_UP_TO_4_DIGIT:
        format = NumberFormatStyle.Currency;
        options.currency = currency || defaultCurrency;
        options.currencyAsSymbol = true;
        options.maximumFractionDigits = 4;
        options.minimumFractionDigits = 0;
        break;
      case DISPLAY_TYPE.PERCENT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 6;
        break;
      case DISPLAY_TYPE.PERCENT_1_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 1;
        options.minimumFractionDigits = 1;
        break;
      case DISPLAY_TYPE.PERCENT_2_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 2;
        options.minimumFractionDigits = 2;
        break;
      case DISPLAY_TYPE.PERCENT_3_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 3;
        options.minimumFractionDigits = 3;
        break;
      case DISPLAY_TYPE.PERCENT_4_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 4;
        options.minimumFractionDigits = 4;
        break;
      case DISPLAY_TYPE.PERCENT_5_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 5;
        options.minimumFractionDigits = 5;
        break;
      case DISPLAY_TYPE.PERCENT_6_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 6;
        options.minimumFractionDigits = 6;
        break;
      case DISPLAY_TYPE.PERCENT_7_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 7;
        options.minimumFractionDigits = 7;
        break;
      case DISPLAY_TYPE.PERCENT_8_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 8;
        options.minimumFractionDigits = 8;
        break;
      case DISPLAY_TYPE.PERCENT_9_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 9;
        options.minimumFractionDigits = 9;
        break;
      case DISPLAY_TYPE.PERCENT_10_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 10;
        options.minimumFractionDigits = 10;
        break;
      case DISPLAY_TYPE.PERCENT_UP_TO_4_DIGIT:
        format = NumberFormatStyle.Percent;
        options.maximumFractionDigits = 4;
        options.minimumFractionDigits = 0;
        break;
      case DISPLAY_TYPE.DECIMAL:
      case DISPLAY_TYPE.NA:
        options.maximumFractionDigits = 6;
        break;
      default:
        options.maximumFractionDigits = 6;
        break;
    }

    if (isPresent(fractionDigits)) {
      options.maximumFractionDigits = fractionDigits;
      options.minimumFractionDigits = fractionDigits;
    }

    let number = NumberFormatter.format(value, locale || 'en-US', format, options);
    if (!locale) {
      if (this.numberFormat.separator === '.' && this.numberFormat.decimal === ',') {
        number = number.replace(/[,.]/g, (m) => {
          return m === ',' ? '.' : ',';
        });
      } else {
        number = number
          .replace(/\,/g, this.numberFormat.separator)
          .replace(/\./g, this.numberFormat.decimal);
      }
    }

    return number.replace(/ /g, ' ');
  }

  public isRtlLang(strLocale: string = this.translate.currentLang) {
    const objLocale = this._parseLocale(strLocale);
    if (!objLocale) {
      return;
    }
    // return true if the intel string lang exists in the BID RTL LANGS array else return false
    return (this._BIDI_RTL_LANGS.indexOf(objLocale.lang) >= 0);
  }

  public changeDateFormat(dateFormat: string) {
    this.dateFormat = dateFormat;
    this.setDateFormat();
    this.changeDateFormatEvent.emit();
  }

  public changeNumberFormat(numberFormat) {
    this.numberFormat = numberFormat;
    this.setNumberFormat();
    this.changeNumberFormatEvent.emit();
  }

  public getDecimalNumberFormat(decimalLimit: number = 2) {
    return {
      mask: createNumberMask({
        prefix: '',
        suffix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: this.numberFormat.separator,
        allowDecimal: true,
        decimalSymbol: this.numberFormat.decimal,
        decimalLimit,
        allowNegative: true
      })
    };
  }

  private _escapeRegExpPattern(str: string): string {
    if (typeof str !== 'string') {
      return str;
    }
    return str.replace(/([\.\*\+\^\$\[\]\\\(\)\|\{\}\,\-\:\?])/g, '\\$1');
  }

  private _toLowerCase(str: any, reserveReturnValue?: string): string {
    if (typeof str !== 'string') {
      return reserveReturnValue || str;
    }
    return str.toLowerCase();
  }

  private _toUpperCase(str: any, reserveReturnValue?: string): string {
    if (typeof str !== 'string') {
      return reserveReturnValue || str;
    }
    return str.toUpperCase();
  }

  private _trim(str: string, delimiter: any, reserveReturnValue?: any) {
    const patterns = [],
      addPatterns = pattern => {
        // Build trim RegExp pattern and push it to patterns array
        patterns.push('^' + pattern + '+|' + pattern + '+$');
      };
    let regexp;

    // fix reserveReturnValue value
    if (typeof delimiter === 'boolean') {
      reserveReturnValue = delimiter;
      delimiter = null;
    }

    if (typeof str !== 'string') {
      return reserveReturnValue && str;
    }

    // Trim based on delimiter array values
    if (Array.isArray(delimiter)) {
      // Loop through delimiter array
      delimiter.map(item => {
        // Escape delimiter to be valid RegExp Pattern
        const pattern = this._escapeRegExpPattern(item);
        // Push pattern to patterns array
        addPatterns(pattern);
      });
    }

    // Trim based on delimiter string value
    if (typeof delimiter === 'string') {
      // Escape delimiter to be valid RegExp Pattern
      const patternDelimiter = this._escapeRegExpPattern(delimiter);
      // push pattern to patterns array
      addPatterns(patternDelimiter);
    }

    // If delimiter  is not defined, Trim white spaces
    if (!delimiter) {
      // Push white space pattern to patterns array
      addPatterns('\\s');
    }

    // Build RegExp pattern
    const pattern = '(' + patterns.join('|') + ')';
    // Build RegExp object
    regexp = new RegExp(pattern, 'g');

    // trim string for all patterns
    while (str.match(regexp)) {
      str = str.replace(regexp, '');
    }

    // Return trim string
    return str;
  }

  private _parseLocale(strLocale: string) {
    // parse locale regex object
    const regex = /^([a-zA-Z]*)([_\-a-zA-Z]*)$/,
      matches = regex.exec(strLocale); // exec regex

    let parsedLocale,
      lang,
      countryCode;

    if (!strLocale || !matches) {
      return;
    }

    // fix countryCode string by trimming '-' and '_'
    matches[2] = this._trim(matches[2], ['-', '_']);

    lang = this._toLowerCase(matches[1]);
    countryCode = this._toUpperCase(matches[2]) || countryCode;

    // object with lang, countryCode properties
    parsedLocale = {
      lang: lang,
      countryCode: countryCode
    };

    // return parsed locale object
    return parsedLocale;
  }

  private setDateFormat() {
    this.dateMask = GP_DATE_MASK[this.dateFormat];
  }

  private setNumberFormat() {
    this.numberDecimalMask = this.getDecimalNumberFormat();

    this.numberIntegerMask = {
      mask: createNumberMask({
        prefix: '',
        suffix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: this.numberFormat.separator,
        allowNegative: true
      })
    };
  }
}
